// src/utils/navHistory.js
//
// Ultra-light in-app navigation tracker.
//
// React Router does not expose "where did I come from?", and the browser
// History API deliberately hides the previous entry's URL. Knowing it is what
// lets an on-screen "Home" control choose between popping the history stack
// (cheap, restores scroll, keeps the stack from growing) and pushing/replacing
// a fresh route.
//
// Cost: one string comparison + one sessionStorage write per route change.
// No React state, no re-renders, no listeners.

const STORAGE_KEY = 'nav:prevPath';

let previousPath = null;
let currentPath =
  typeof window !== 'undefined' ? window.location.pathname : '/';

/**
 * Record a completed navigation. Call once per location change.
 * @param {string} pathname
 */
export function recordNavigation(pathname) {
  if (!pathname || pathname === currentPath) return;
  previousPath = currentPath;
  currentPath = pathname;
  try {
    sessionStorage.setItem(STORAGE_KEY, previousPath);
  } catch {
    /* private mode / storage disabled — in-memory value still works */
  }
}

/**
 * The pathname the user was on immediately before the current one.
 * Returns null on a cold start (deep link, hard refresh, app launch).
 * @returns {string|null}
 */
export function getPreviousPath() {
  if (previousPath !== null) return previousPath;
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * True when there is a real history entry behind the current one, i.e.
 * `navigate(-1)` will actually move rather than silently no-op.
 *
 * React Router v6 stamps an incrementing `idx` onto history.state; when it is
 * 0 we are on the first entry of this session (cold launch / deep link).
 * @returns {boolean}
 */
export function canGoBack() {
  if (typeof window === 'undefined') return false;
  const idx = window.history.state?.idx;
  if (typeof idx === 'number') return idx > 0;
  return window.history.length > 1;
}

/** Routes that render the Home screen. */
export const HOME_PATHS = ['/', '/home'];

/** @param {string|null} path */
export const isHomePath = (path) => !!path && HOME_PATHS.includes(path);
